import React, { useEffect, useCallback, useRef, useState } from "react";
import { useOutlookActions } from "./useOutlookActions";
import { useOutlookStore } from "../stores/outlookStore";
import { usePlatform } from "@/pages/taskpane/shared/hooks";
import { WebAddinPlatforms } from "@/pages/taskpane/shared/hooks/usePlatform";
import { generateRecurrenceUtcOccurrences } from "../../utils/occurrence";
import { useAppointmentStore, useResourceStore } from "@/pages/taskpane/shared/stores";
import { useUpdateTemp } from "@/pages/taskpane/features/resources/services/useUpdateTemp";
import { isValidEmailAddress } from "../../utils/validate";
import { getOrganizerFromAppointment } from "../utils";
import { ChildResourcesEntity } from "@/pages/taskpane/features/resources/services/types/resourceEntity";

declare const Office: any;

export const useOutlookAppointmentEvents = () => {
  const {
    setAppointmentTimeWithZone,
    setEnhancedLocation,
    setOrganizer,
    setRequiredAttendees,
    setOptionalAttendees,
    getAll: getAppointmentStoreData,
    initializeAppointmentTime,
    setRecurringDates,
    setTimeZone,
    setIsNoEndDateRecurring,
    setRecurrence,
  } = useOutlookStore();

  const { syncFromOutlook } = useAppointmentStore()
  const { getCurrentPlatform, isSetSupported } = usePlatform();
  const { InitializeAppointmentData, RemoveResources, AddResources } = useOutlookActions();
  const {setIsCheckSyncResource} = useResourceStore();

  const { mutate: updateTemp } = useUpdateTemp({});

  // State management
  const isSubscribed = useRef(false);
  const [eventStatus, setEventStatus] = useState({
    recipients: false,
    timeChange: false,
    enhancedLocations: false,
    recurrence: false,
  });

  // Debouncing refs
  const recipientsDebounceRef = useRef<NodeJS.Timeout>();
  const timeChangeDebounceRef = useRef<NodeJS.Timeout>();

  const loadSession = useCallback((callbacks: any) => {
    try {
      let item = Office.context.mailbox.item;
      if (item && item.itemType === Office.MailboxEnums.ItemType.Appointment) {
        let appCompose = item as Office.AppointmentCompose;
        if (appCompose) {
          appCompose.sessionData.getAllAsync(callbacks);
        }
      }
    } catch (error) {
      console.error("Failed to load session:", error);
    }
  }, []);

  const loadSessionCallback = useCallback((asyncResult: Office.AsyncResult<Office.SessionData>) => {
    if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
      console.error("Failed to load session data:", asyncResult.error);
    }
  }, []);

  // Utility function for debouncing
  const debounce = useCallback(
    (func: Function, delay: number, timeoutRef: React.MutableRefObject<NodeJS.Timeout | undefined>) => {
      return (...args: any[]) => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => func(...args), delay);
      };
    },
    []
  );

  // Time change handler with debouncing - proper event handler signature
  const handleAppointmentTimeChangeInternal = useCallback(
    (eventArgs: Office.AppointmentTimeChangedEventArgs) => {
      try {
        setIsCheckSyncResource(true);
        // console.log("⏰ TIME CHANGED EVENT FIRED!", eventArgs);
        // console.log("🔍 Event data types:", {
        //   startType: typeof eventArgs?.start,
        //   endType: typeof eventArgs?.end,
        //   startValue: eventArgs?.start,
        //   endValue: eventArgs?.end
        // });

        // ALWAYS get the current times from the appointment item
        // because eventArgs may contain strings or unreliable data
        const item = Office.context.mailbox.item;
        if (item && item.itemType === Office.MailboxEnums.ItemType.Appointment) {
          const appCompose = item as Office.AppointmentCompose;
          if (appCompose) {
            // console.log("📍 TIME CHANGE: Fetching fresh times from Office.js...");
            Promise.all([
              new Promise<Date>((resolve, reject) => {
                appCompose.start.getAsync((result: Office.AsyncResult<Date>) => {
                  if (result.status === Office.AsyncResultStatus.Succeeded) {
                    // console.log("✅ TIME CHANGE: Got start time:", result.value);
                    resolve(result.value);
                  } else {
                    reject(new Error(`Failed to get start time: ${result.error?.message}`));
                  }
                });
              }),
              new Promise<Date>((resolve, reject) => {
                appCompose.end.getAsync((result: Office.AsyncResult<Date>) => {
                  if (result.status === Office.AsyncResultStatus.Succeeded) {
                    // console.log("✅ TIME CHANGE: Got end time:", result.value);
                    resolve(result.value);
                  } else {
                    reject(new Error(`Failed to get end time: ${result.error?.message}`));
                  }
                });
              }),
            ])
              .then(([startTime, endTime]) => {
                // console.log("📅 TIME CHANGE: Times fetched successfully", { startTime, endTime });

                // Convert to local client time
                let startDateTimeZone = null;
                let endDateTimeZone = null;

                try {
                  if (startTime && Office?.context?.mailbox?.convertToLocalClientTime) {
                    startDateTimeZone = Office.context.mailbox.convertToLocalClientTime(startTime);
                    // console.log("🌐 TIME CHANGE: Converted start time to local:", startDateTimeZone);
                  }

                  if (endTime && Office?.context?.mailbox?.convertToLocalClientTime) {
                    endDateTimeZone = Office.context.mailbox.convertToLocalClientTime(endTime);
                    // console.log("🌐 TIME CHANGE: Converted end time to local:", endDateTimeZone);
                  }
                } catch (error) {
                  console.warn("⚠️ TIME CHANGE: Failed to convert to local client time:", error);
                }

                // Get and update timezone
                try {
                  const userTimeZone = Office?.context?.mailbox?.userProfile?.timeZone ||
                    Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
                  // console.log("🌍 TIME CHANGE: Got timezone:", userTimeZone);
                  setTimeZone(userTimeZone);
                  syncFromOutlook(startTime, endTime, startDateTimeZone, endDateTimeZone, userTimeZone);
                } catch (error) {
                  console.warn("⚠️ TIME CHANGE: Failed to get timezone:", error);
                }

                // console.log("📅 TIME CHANGE: Updating outlookStore with:", {
                //   startTime,
                //   endTime,
                //   startDateTimeZone,
                //   endDateTimeZone
                // });

                setAppointmentTimeWithZone(startTime, endTime, startDateTimeZone, endDateTimeZone);
                setEventStatus((prev) => ({ ...prev, timeChange: true }));

                // Update temp booking với thời gian mới
                const uid = useOutlookStore.getState().customUid || useOutlookStore.getState().iCalUId || '';
                if (uid) {
                  updateTemp({
                    uid,
                    startDate: startTime.toISOString(),
                    endDate: endTime.toISOString(),
                  });
                }
              })
              .catch((error) => {
                console.error("❌ TIME CHANGE: Failed to get appointment times:", error);
              });
          } else {
            console.error("❌ TIME CHANGE: Not an appointment item or item not available");
          }
        }
      } catch (error) {
        console.error("Error in appointment time change handler:", error);
      }
    },
    [setAppointmentTimeWithZone, setEventStatus, setTimeZone, syncFromOutlook, updateTemp]
  );

  // Debounced time change handler
  const handleAppointmentTimeChange = useCallback(
    debounce(handleAppointmentTimeChangeInternal, 500, timeChangeDebounceRef),
    [handleAppointmentTimeChangeInternal, debounce]
  );

  // Enhanced locations change handler
  const handleEnhancedLocationsChange = useCallback(
    async (asyncResult: Office.EnhancedLocationsChangedEventArgs) => {
      // debugger;
      try {
        setIsCheckSyncResource(true);
        // console.log("📍 ENHANCED LOCATIONS CHANGED EVENT FIRED!", asyncResult);
        const currentPlatform = getCurrentPlatform();
        const isMailbox18Supported = isSetSupported("Mailbox", "1.8");
        const isPlatformMac = currentPlatform === WebAddinPlatforms.OutlookForMac;

        // Only handle enhanced locations for supported platforms
        if (!isMailbox18Supported || isPlatformMac) {
          return;
        }

        const locations = asyncResult?.enhancedLocations || [];
        // console.log("📍 Enhanced locations data:", locations);

        let enhancedLocations: Office.LocationDetails[] = []
        locations?.forEach((location) => {
          enhancedLocations.push({
            displayName: location.displayName,
            emailAddress: location.emailAddress || location.locationIdentifier?.id || "",
            locationIdentifier: {
              id: location.locationIdentifier?.id,
              type: location.locationIdentifier?.type,
            },
          })
        });

        // Case xóa 1 resource con của VR từ outlook -> xóa resource con khác của VR trong outlook và VR trong addin
        // const oldEnhancedLocations = useOutlookStore.getState().enhancedLocation;
        // const newLocationEmailsSet = new Set(enhancedLocations.map(l => l.emailAddress));
        // const enhancedLocationsRemoved = oldEnhancedLocations.filter(
        //   (location) => !newLocationEmailsSet.has(location.emailAddress)
        // );
        // const locationsNeedToRemove: string[] = [];

        // if (enhancedLocationsRemoved.length > 0) {
        //   const removedEmailsSet = new Set(enhancedLocationsRemoved.map(l => l.emailAddress));
        //   const currentSelectedResource = useResourceStore.getState().selectedResource ?? [];
        //   const resourceNeedToRemove = currentSelectedResource.filter(r =>
        //     r?.childResources?.some(c => removedEmailsSet.has(c.email))
        //   );

        //   resourceNeedToRemove.forEach(r => {
        //     r?.childResources?.forEach(c => {
        //       if (!removedEmailsSet.has(c.email)) {
        //         locationsNeedToRemove.push(c.email);
        //       }
        //     });
        //   });

        //   if (resourceNeedToRemove.length > 0) {
        //     await RemoveResources(resourceNeedToRemove);
        //   }
        // }
        // if (locationsNeedToRemove.length > 0) {
        //   const locationsToRemoveSet = new Set(locationsNeedToRemove);
        //   enhancedLocations = enhancedLocations.filter(l => !locationsToRemoveSet.has(l.emailAddress));
        // }

        // console.log("🏢 Enhanced locations:", enhancedLocations);
        setEnhancedLocation(enhancedLocations);
      } catch (error) {
        console.error("Error in enhanced locations change handler:", error);
      }
    },
    [getCurrentPlatform, isSetSupported, setEnhancedLocation]
  );

  // Recipients change handler internal logic
  const handleRecipientsChangeInternal = useCallback(
    async (eventArgs: Office.RecipientsChangedEventArgs) => {
      try {
        setIsCheckSyncResource(true);
        // console.log("👥 RECIPIENTS CHANGED EVENT FIRED!", eventArgs);
        // const organizerEmail = Office.context.mailbox.userProfile.emailAddress?.toLowerCase() || "";
        // const organizerName = Office.context.mailbox.userProfile.displayName || "";
        // console.log("📧 Setting organizer:", organizerEmail);

        // Set organizer in AppointmentStore
        // setOrganizer(organizerEmail, organizerName);

        // Get all current recipients from the appointment item
        const item = Office.context.mailbox.item;
        if (item && item.itemType === Office.MailboxEnums.ItemType.Appointment) {
          const appCompose = item as Office.AppointmentCompose;

          // Get organizer from appointment item (supports shared mailbox)
          const { email: organizerEmail, name: organizerName } = await getOrganizerFromAppointment(appCompose);
          // console.log("📧 Setting organizer:", organizerEmail);

          // Set organizer in AppointmentStore
          setOrganizer(organizerEmail, organizerName);

          // Process required attendees
          appCompose.requiredAttendees.getAsync((result: Office.AsyncResult<Office.EmailAddressDetails[]>) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              const recipients = (result.value || []).filter(
                (recipient) => {
                  // Filter out organizer and invalid email addresses
                  if (!recipient.emailAddress) return false;
                  if (recipient.emailAddress.toLowerCase() === organizerEmail) return false;
                  return isValidEmailAddress(recipient.emailAddress);
                }
              );

              // Convert to OutlookRecipient format for AppointmentStore
              const requiredAttendees = recipients.map((r) => ({
                Name: r.displayName || r.emailAddress || "",
                Email: r.emailAddress || "",
                RecipientType: r.recipientType == "user" ? 0 : 2,
              }));
              // console.log("✅ Setting required attendees:", requiredAttendees);
              setRequiredAttendees(requiredAttendees);
              setEventStatus((prev) => ({ ...prev, recipients: true }));
            }
          });

          // Process optional attendees
          appCompose.optionalAttendees.getAsync((result: Office.AsyncResult<Office.EmailAddressDetails[]>) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              const recipients = (result.value || []).filter(
                (recipient) => {
                  // Filter out organizer and invalid email addresses
                  if (!recipient.emailAddress) return false;
                  if (recipient.emailAddress.toLowerCase() === organizerEmail) return false;
                  return isValidEmailAddress(recipient.emailAddress);
                }
              );

              // Convert to OutlookRecipient format for AppointmentStore
              const optionalAttendees = recipients.map((r) => ({
                Name: r.displayName || r.emailAddress || "",
                Email: r.emailAddress || "",
                RecipientType: r.recipientType == "user" ? 0 : 2,
              }));
              // console.log("✅ Setting optional attendees:", optionalAttendees);
              setOptionalAttendees(optionalAttendees);
            }
          });
        } else {
          // Fallback if not appointment item
          const organizerEmail = Office.context.mailbox.userProfile.emailAddress?.toLowerCase() || "";
          const organizerName = Office.context.mailbox.userProfile.displayName || "";
          setOrganizer(organizerEmail, organizerName);
        }
      } catch (error) {
        console.error("Error in recipients change handler:", error);
      }
    },
    [setRequiredAttendees, setOptionalAttendees, setEventStatus, setOrganizer]
  );

  // Debounced recipients change handler
  const handleRecipientsChange = useCallback(debounce(handleRecipientsChangeInternal, 300, recipientsDebounceRef), [
    handleRecipientsChangeInternal,
    debounce,
  ]);

  // Recurrence change handler
  const handleRecurrenceChange = useCallback((asyncResult: Office.RecurrenceChangedEventArgs) => {
    try {
      setIsCheckSyncResource(true);
      const item = Office.context.mailbox.item;
      if (item && item.itemType === Office.MailboxEnums.ItemType.Appointment) {
        const appCompose = item as Office.AppointmentCompose;

        appCompose.recurrence.getAsync((result: Office.AsyncResult<Office.Recurrence>) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            const recurrence = result.value as any;
            setRecurrence(recurrence);
            const seriesId = appCompose.seriesId;
            if (recurrence && !seriesId) {
              try {
                const recurringDates = generateRecurrenceUtcOccurrences(recurrence, 50);
                setRecurringDates(recurringDates);
                setIsNoEndDateRecurring(!Boolean(recurrence?.seriesTime?.endDay && recurrence?.seriesTime?.endMonth && recurrence?.seriesTime?.endYear));
              } catch (error) {
                console.warn("⚠️ Failed to get recurring dates:", error);
                setRecurringDates([]);
                setIsNoEndDateRecurring(false);
              }
            } else {
              setRecurringDates([]);
              setIsNoEndDateRecurring(false);
            }
          }
        });
      }
    } catch (error) {
      console.error("Error in recurrence change handler:", error);
    }
  }, [getAppointmentStoreData, setRecurringDates, setIsNoEndDateRecurring]);

  // Note: Subject change events are not available for appointment compose items
  // They are only available in read scenarios, not compose scenarios
  // Subject will be read directly from Outlook API when needed (e.g., when sending message)

  // Subscribe to recipients changes using Office.js events
  const subscribeToRecipientsChange = useCallback(() => {
    try {
      // console.log("🔥 Subscribing to recipients changes...");

      // Try to subscribe to recipients changed event
      Office.context.mailbox.item.addHandlerAsync(
        Office.EventType.RecipientsChanged,
        handleRecipientsChange,
        (result: Office.AsyncResult<void>) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            // console.log("✅ Recipients change subscription successful", result);
            setEventStatus((prev) => ({ ...prev, recipients: true }));
          } else {
            console.error("❌ Recipients change subscription failed:", result.error);
          }
        }
      );
    } catch (error) {
      console.error("❌ Exception in recipients subscription:", error);
    }
  }, [handleRecipientsChange, setEventStatus]);

  // Subscribe to time changes using Office.js events
  const subscribeToTimeChange = useCallback(() => {
    try {
      // console.log("🔥 Subscribing to time changes...");

      // Try to subscribe to appointment time changed event
      Office.context.mailbox.item.addHandlerAsync(
        Office.EventType.AppointmentTimeChanged,
        handleAppointmentTimeChange,
        (result: Office.AsyncResult<void>) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            // console.log("✅ Time change subscription successful");
            setEventStatus((prev) => ({ ...prev, timeChange: true }));
          } else {
            console.error("❌ Time change subscription failed:", result.error);
          }
        }
      );
    } catch (error) {
      console.error("❌ Exception in time change subscription:", error);
    }
  }, [handleAppointmentTimeChange, setEventStatus]);

  // Subscribe to enhanced locations changes using Office.js events
  const subscribeToEnhancedLocations = useCallback(() => {
    try {
      // console.log("🔥 Subscribing to enhanced locations...");

      // Try to subscribe to enhanced locations changed event
      Office.context.mailbox.item.addHandlerAsync(
        Office.EventType.EnhancedLocationsChanged,
        handleEnhancedLocationsChange,
        (result: Office.AsyncResult<void>) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            // console.log("✅ Enhanced locations subscription successful");
            setEventStatus((prev) => ({ ...prev, enhancedLocations: true }));
          } else {
            console.error("❌ Enhanced locations subscription failed:", result.error);
          }
        }
      );
    } catch (error) {
      console.error("❌ Exception in enhanced locations subscription:", error);
    }
  }, [handleEnhancedLocationsChange, setEventStatus]);

  // Subscribe to recurrence changes using Office.js events
  const subscribeToRecurrenceChange = useCallback(() => {
    try {
      // console.log("🔥 Subscribing to recurrence changes...");

      // Try to subscribe to recurrence changed event
      Office.context.mailbox.item.addHandlerAsync(
        Office.EventType.RecurrenceChanged,
        handleRecurrenceChange,
        (result: Office.AsyncResult<void>) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            // console.log("✅ Recurrence change subscription successful");
            setEventStatus((prev) => ({ ...prev, recurrence: true }));
          } else {
            console.error("❌ Recurrence change subscription failed:", result.error);
          }
        }
      );
    } catch (error) {
      console.error("❌ Exception in recurrence subscription:", error);
    }
  }, [handleRecurrenceChange, setEventStatus]);

  // Initialize current data from Outlook
  const initializeCurrentData = useCallback(async () => {
    try {
      // console.log("🔄 Initializing current appointment data...");

      // Use the comprehensive InitializeAppointmentData function
      const success = await InitializeAppointmentData();
      if (success) {
        // console.log("✅ Appointment data initialized successfully");

        // Also trigger recipients load to ensure everything is in sync
        handleRecipientsChangeInternal({} as Office.RecipientsChangedEventArgs);
      } else {
        console.warn("⚠️ Failed to initialize appointment data, using fallback...");

        // Fallback: try to initialize basic appointment time
        // await initializeAppointmentTime();

        // Trigger initial recipients load
        handleRecipientsChangeInternal({} as Office.RecipientsChangedEventArgs);
      }
    } catch (error) {
      console.error("❌ Error initializing current data:", error);

      // Final fallback: try store's initializeAppointmentTime
      try {
        await initializeAppointmentTime();
      } catch (fallbackError) {
        console.error("❌ Fallback initialization also failed:", fallbackError);
      }
    }
  }, [InitializeAppointmentData, initializeAppointmentTime, handleRecipientsChangeInternal]);

  const initializeEventSubscriptions = useCallback(async () => {
    // console.log("🚀 Initializing event subscriptions...");

    // Check Office.js availability
    if (!Office || !Office.context || !Office.context.mailbox || !Office.context.mailbox.item) {
      console.error("❌ Office.js context not available!");
      return;
    }

    const item = Office.context.mailbox.item;
    // console.log("📧 Item type:", item?.itemType, "Expected:", Office.MailboxEnums.ItemType.Appointment);

    if (!item || item.itemType !== Office.MailboxEnums.ItemType.Appointment) {
      console.error("❌ Not an appointment item");
      return;
    }

    // Load session
    loadSession(loadSessionCallback);

    // Initialize organizer email
    // const organizerEmail = Office.context.mailbox.userProfile.emailAddress || "";
    // const organizerName = Office.context.mailbox.userProfile.displayName || "";
    const { email: organizerEmail, name: organizerName } = await getOrganizerFromAppointment(item as Office.AppointmentCompose);
    // console.log("🎯 Setting organizer:", organizerEmail);
    setOrganizer(organizerEmail, organizerName);

    // Subscribe to all available events
    // console.log("📋 Starting event subscriptions...");
    subscribeToRecipientsChange();
    subscribeToTimeChange();
    subscribeToEnhancedLocations();
    subscribeToRecurrenceChange();

    // Initialize current data (async)
    await initializeCurrentData();

    // console.log("✅ Event subscription initialization completed");
  }, [
    loadSession,
    loadSessionCallback,
    subscribeToRecipientsChange,
    subscribeToTimeChange,
    subscribeToEnhancedLocations,
    subscribeToRecurrenceChange,
    initializeCurrentData,
    setOrganizer,
  ]);

  useEffect(() => {
    // console.log("🎯 useOutlookAppointmentEvents useEffect called!");

    // Prevent multiple subscriptions
    if (isSubscribed.current) {
      // console.log("⚠️ Already subscribed, skipping...");
      return;
    }

    // Add a small delay to ensure Office.js is fully initialized
    const timeoutId = setTimeout(async () => {
      try {
        await initializeEventSubscriptions();
        isSubscribed.current = true;
      } catch (error) {
        console.error("❌ Failed to initialize event subscriptions:", error);
      }
    }, 100);

    // Cleanup function
    return () => {
      try {
        // console.log("🧹 Cleaning up event subscriptions...");
        clearTimeout(timeoutId);

        // Clear all debounce timeouts
        if (recipientsDebounceRef.current) clearTimeout(recipientsDebounceRef.current);
        if (timeChangeDebounceRef.current) clearTimeout(timeChangeDebounceRef.current);

        isSubscribed.current = false;
        // console.log("✅ Cleanup completed");
        // Note: Office.js doesn't provide a direct way to remove event handlers
        // The handlers will be automatically cleaned up when the add-in is unloaded
      } catch (error) {
        console.error("❌ Error during cleanup:", error);
      }
    };
  }, [initializeEventSubscriptions]);

  // Manual trigger for testing
  const triggerRecipientsCheck = useCallback(() => {
    handleRecipientsChangeInternal({} as Office.RecipientsChangedEventArgs);
  }, [handleRecipientsChangeInternal]);

  return {
    // Event subscription status
    isSubscribed: isSubscribed.current,
    eventStatus,

    // Manual test triggers
    triggerRecipientsCheck,

    // Store data getter for debugging
    getStoreData: getAppointmentStoreData,
  };
};
